import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  CardActions,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  Grid,
  Modal,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  ReadMore as ReadMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { API_URL, createAxiosInstance } from '../../../api';
import Swal from 'sweetalert2';
import successIcon from '../../../assets/success-icon.svg';
import errorIcon from '../../../assets/error-icon.svg';
import { getCourseImageUrl } from '../../../utils/imageUtils';
import ImageWithSkeleton from '../../../Components/ImageWithSkeleton';
import circleContainer from '../../../assets/images/circle-container.svg';
import CourseBilling from '../../CourseBilling/CourseBilling';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreateCourseContext } from '../../AddCourse/AddCourseContext/AddContextContext';

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublicKey);

// RenewCourseCard component - matches EnhancedCourseCard design
const RenewCourseCard = ({ course, onRenew, onReadMore }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (course) => {
    if (course.isDeleted) return 'error';
    if (course.isExpire) return 'warning';
    if (course.isDisabled) return 'warning';
    if (course.isApproved) return 'success';
    return 'info'; // Pending approval
  };

  const getStatusLabel = (course) => {
    if (course.isDeleted) return 'Deleted';
    if (course.isExpire) return 'Expired';
    if (course.isDisabled) return 'Disabled';
    if (course.isApproved) return 'Live';
    return 'Pending Review';
  };

  // Parse course types - handle both string and array formats
  const courseTypes = course.types ? 
    (Array.isArray(course.types) ? course.types : course.types.split(',')) : [];

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      overflow: 'hidden',
      background: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
      transition: 'box-shadow 0.2s ease',
    }}>
      {/* Course Image with overlayed status */}
      <Box sx={{ position: 'relative' }}>
        <ImageWithSkeleton
          src={getCourseImageUrl(course.image)}
          alt={course.name || 'Course image'}
          height={180}
          onErrorSrc={'/assets/course-place1.png'}
          sx={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', objectFit: 'cover' }}
        />
        <Chip 
          label={getStatusLabel(course)} 
          color={getStatusColor(course)}
          size="small"
          sx={{ 
            position: 'absolute',
            top: 10,
            right: 10,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            borderRadius: 1.5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            '& .MuiChip-label': { px: 1.25 }
          }}
        />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {course.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {course.subject} • {course.level}
            </Typography>
          </Box>
          {/* Compact price badge */}
          <Box sx={{ position: 'relative', width: 56, height: 56, ml: 1, flexShrink: 0 }}>
            <img 
              src={circleContainer} 
              alt="Price Circle" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              lineHeight: 1.1
            }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#712d94', fontSize: '0.8rem' }}>
                {course.coursePrice && course.coursePrice !== '0' && course.coursePrice !== 0 ? `£${course.coursePrice}` : 'Free'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#712d94', fontSize: '0.5rem' }}>Price</Typography>
            </Box>
          </Box>
        </Box>

        {/* Course Details - Compact */}
        <Box sx={{ mb: 1.5, flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Subject:</strong> {course.subject || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Level:</strong> {course.level || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Location:</strong> {courseTypes.length > 0 ? courseTypes.join(' / ') : 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Date:</strong> {course.isOngoing ? 'Anytime' : `${formatDate(course.date?.[0])} - ${formatDate(course.date?.[1])}`}
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Created: {formatDate(course.createdAt)}
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 1, gap: 1.25, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'stretch' }}>
        <Button
          variant="outlined"
          startIcon={<ReadMoreIcon />}
          onClick={onReadMore}
          sx={{
            flex: 1,
            minWidth: 0,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            py: 0.9,
            px: 1.5,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              marginRight: '6px',
            },
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: theme.palette.primary.main + '08',
            }
          }}
        >
          Read More
        </Button>
        <Tooltip title="Renew Course" arrow>
          <IconButton
            onClick={() => onRenew(course)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const RenewCourseTab = React.memo(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.main.userId);
  const token = useSelector((state) => state.main.token);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [renewing, setRenewing] = useState(false);

  // Helper: determine if a course is eligible for renewal
  const isCourseEligibleForRenewal = (course) => {
    // Exclude deleted or disabled courses
    if (course.isDeleted || course.isDisabled) return false;

    // If backend has already marked it expired, always allow renewal
    if (course.isExpire) return true;

    // Exclude approved and live courses (unless expired)
    if (course.isApproved && !course.isExpire) return false;

    if (course.expirationDate) {
      const expirationDate = new Date(course.expirationDate);
      if (isNaN(expirationDate.getTime())) return false;

      const now = new Date();
      const diffMs = expirationDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      // If pending review with future expiration date more than 7 days away, exclude (recently renewed)
      if (!course.isApproved && diffDays > 7) return false;

      // Expired or within 7 days of expiring
      const RENEW_THRESHOLD_DAYS = 7;
      return diffDays <= 0 || diffDays <= RENEW_THRESHOLD_DAYS;
    }

    return false;
  };

  // Fetch tutor's courses and filter to those that are expired or close to expiry
  const fetchCourses = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const axiosInstance = createAxiosInstance();

      // Fetch active and expired courses separately
      const [activeRes, expiredRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/api/courses/coursesUser/${userId}/false`),
        axiosInstance.get(`${API_URL}/api/courses/coursesUser/${userId}/true`)
      ]);

      const allCourses = [
        ...(activeRes.data || []),
        ...(expiredRes.data || [])
      ];

      // Keep only courses that are expired or close to expiry
      const eligibleCourses = allCourses.filter(isCourseEligibleForRenewal);

      setCourses(eligibleCourses);
      
    } catch (error) {
      console.error('Error fetching courses for renewal:', error);
      if (error.response?.status >= 500) {
        setError('Failed to load courses. Please try again.');
      } else {
        setCourses([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleRenewCourse = async (course) => {
    if (!course || !course._id) return;
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${API_URL}/api/courses/${course._id}`);
      const courseDataArray = Array.isArray(response.data) ? response.data : [response.data];
      const fullCourseData = courseDataArray.length > 0 ? courseDataArray[0] : course;

      // Define the mapping from advPrice to duration key
      const priceToDurationMap = {
        '£4.99': '1',
        '£7.99': '2',
        '£12.99': '6',
        '£19.99': '12',
      };

      // Determine the correct duration
      let actualDuration = fullCourseData.duration;
      if (actualDuration === "Duration of Advertising" || !actualDuration) {
        const cleanedAdvPrice = fullCourseData.advPrice?.replace('£', '');
        actualDuration = priceToDurationMap[`£${cleanedAdvPrice}`] || '1'; // Default to '1' if no match
      }
      
      setSelectedCourse({
        ...fullCourseData,
        advPrice: fullCourseData.advPrice || course.advPrice || '10',
        duration: actualDuration, // Use the derived or existing duration
      });
      setIsPaymentModalOpen(true);
    } catch (error) {
      console.error('Error fetching course details:', error);
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: "Failed to load course details. Please try again.",
        confirmButtonColor: "#712d94",
      });
    }
  };

  const handlePaymentSuccess = async () => {
    if (!selectedCourse || !selectedCourse._id) {
      console.error('No course selected for renewal');
      return;
    }
    setRenewing(true);
    try {
      const formData = new FormData();
      const courseDataForRenewal = { ...selectedCourse };

      if (typeof courseDataForRenewal.image === 'string') {
        delete courseDataForRenewal.image;
      }
      if (typeof courseDataForRenewal.file === 'string') {
        delete courseDataForRenewal.file;
      }

      // Ensure required fields are present and duration is numeric
      courseDataForRenewal.userId = userId;
      courseDataForRenewal.advPrice = selectedCourse.advPrice || '10';
      
      // Re-derive duration here as well to be safe
      const priceToDurationMap = {
        '£4.99': '1',
        '£7.99': '2',
        '£12.99': '6',
        '£19.99': '12',
      };
      let actualDuration = selectedCourse.duration;
      if (actualDuration === "Duration of Advertising" || !actualDuration) {
        const cleanedAdvPrice = selectedCourse.advPrice?.replace('£', '');
        actualDuration = priceToDurationMap[`£${cleanedAdvPrice}`] || '1';
      }
      courseDataForRenewal.duration = actualDuration; // Ensure numeric duration is sent

      formData.append("courseData", JSON.stringify(courseDataForRenewal));
      formData.append("paymentStatus", "success");

      const axiosInstance = createAxiosInstance();
      await axiosInstance.put(
        `${API_URL}/api/courses/renew/${selectedCourse._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIsPaymentModalOpen(false);
      setSelectedCourse(null);
      
      Swal.fire({
        imageUrl: successIcon,
        imageHeight: "100px",
        title: "Course Renewed Successfully!",
        html: "Your course has been renewed and is pending admin review.",
        width: 600,
        confirmButtonColor: "#712d94",
      });

      // Refresh the course list
      fetchCourses();
    } catch (error) {
      console.error('Error renewing course:', error);
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: error?.response?.data?.message || "Failed to renew course. Please try again.",
        confirmButtonColor: "#712d94",
      });
    } finally {
      setRenewing(false);
    }
  };

  const handleReadMore = (course) => {
    navigate(`/course-detail/${course._id}`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '400px',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading your courses for renewal...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <CreateCourseContext.Provider value={{ 
      sendReq: false, 
      setsendReq: () => {},
      coursePrice: selectedCourse?.coursePrice || '',
      setCoursePrice: () => {},
      isPaymentSuccessful: false,
      setIsPaymentSuccessful: () => {}
    }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Renew Course
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a course advert below to renew its advertising period.
          </Typography>
        </Box>

        {courses.length === 0 ? (
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '2px dashed #e2e8f0'
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  gap: 2
                }}
              >
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                  No Courses Available to Renew
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  You currently have no courses eligible for renewal. Courses that are expired or expiring within 7 days will appear here.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2.5}>
            {courses.map((course) => (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={course._id || course.id}>
                <RenewCourseCard
                  course={course}
                  onRenew={handleRenewCourse}
                  onReadMore={() => handleReadMore(course)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Payment Modal */}
        <Modal
          open={isPaymentModalOpen}
          onClose={() => {
            if (!renewing) {
              setIsPaymentModalOpen(false);
              setSelectedCourse(null);
            }
          }}
          aria-labelledby="payment-modal-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            '&:focus': {
              outline: 'none'
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 0,
              overflow: 'auto',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => {
                if (!renewing) {
                  setIsPaymentModalOpen(false);
                  setSelectedCourse(null);
                }
              }}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Payment Content */}
            <Elements stripe={stripePromise}>
              <CourseBilling
                advancePrice={selectedCourse?.advPrice}
                duration={selectedCourse?.duration}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentFailure={() => {
                  setIsPaymentModalOpen(false);
                  setSelectedCourse(null);
                }}
                isRenewal={true}
              />
            </Elements>
          </Box>
        </Modal>
      </Box>
    </CreateCourseContext.Provider>
  );
});

RenewCourseTab.displayName = 'RenewCourseTab';

export default RenewCourseTab;
